import {
  Assignment,
  Business,
  CalendarToday,
  ExpandMore,
  FilterList,
  People,
  Person,
  PlayArrow,
  Schedule,
  Star,
  Task,
  Warning,
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Fade,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import ObrigacaoModal from "../components/ObrigacaoModal";
import TarefaModal from "../components/TarefaModal";
import { useAuth } from "../context/AuthContext";
import {
  agendaService,
  clienteService,
  obrigacaoService,
  recorrenciaService,
  setorService,
  tarefaService,
  tipoObrigacaoService,
  usuarioService,
} from "../services/api";

const Agenda = () => {
  const { user } = useAuth();
  const [agenda, setAgenda] = useState({});
  const [estatisticas, setEstatisticas] = useState({});
  const [tarefas, setTarefas] = useState([]);
  const [estatisticasTarefas, setEstatisticasTarefas] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filtroVisibilidade, setFiltroVisibilidade] = useState("ambos"); // "tarefas", "obrigacoes", "ambos"
  const [filtroUsuario, setFiltroUsuario] = useState(""); // Filtro por usuário responsável
  const [usuarios, setUsuarios] = useState([]); // Lista de usuários para o filtro

  // Estados para o modal de edição de tarefas
  const [openTarefaDialog, setOpenTarefaDialog] = useState(false);
  const [editingTarefa, setEditingTarefa] = useState(null);
  const [salvandoTarefa, setSalvandoTarefa] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Estado do formulário de tarefa
  const [formDataTarefa, setFormDataTarefa] = useState({
    cliente_id: "",
    descricao: "",
    data_vencimento: new Date(),
    hora_vencimento: new Date(),
    status: "pendente",
    responsavel_id: "",
    priorizar: false,
    comentarios: "",
  });

  // Estados para o modal de edição de obrigações
  const [openObrigacaoDialog, setOpenObrigacaoDialog] = useState(false);
  const [editingObrigacao, setEditingObrigacao] = useState(null);
  const [salvandoObrigacao, setSalvandoObrigacao] = useState(false);
  const [tiposObrigacao, setTiposObrigacao] = useState([]);
  const [setores, setSetores] = useState([]);
  const [errorsObrigacao, setErrorsObrigacao] = useState({});

  // Estado do formulário de obrigação
  const [formDataObrigacao, setFormDataObrigacao] = useState({
    cliente_id: "",
    tipo_obrigacao_id: "",
    setor_id: "",
    comentarios: "",
    data_vencimento: "",
    status: "pendente",
  });

  // Função para obter o primeiro e último dia do mês atual
  const obterDatasMesAtual = () => {
    const agora = new Date();
    const primeiroDia = new Date(agora.getFullYear(), agora.getMonth(), 1);
    const ultimoDia = new Date(agora.getFullYear(), agora.getMonth() + 1, 0);

    return {
      data_inicio: primeiroDia.toISOString().split("T")[0],
      data_fim: ultimoDia.toISOString().split("T")[0],
    };
  };

  const [filtroDataObrigacoes, setFiltroDataObrigacoes] = useState(() =>
    obterDatasMesAtual(),
  );

  // Estado para armazenar os filtros aplicados (separado dos filtros editáveis)
  const [filtrosAplicados, setFiltrosAplicados] = useState(() => ({
    datas: obterDatasMesAtual(),
    usuario: "",
  }));

  useEffect(() => {
    carregarDados();
  }, [filtrosAplicados]); // eslint-disable-line react-hooks/exhaustive-deps

  // Atualizar filtro de usuário automaticamente (mantém o comportamento original)
  useEffect(() => {
    setFiltrosAplicados((prev) => ({
      ...prev,
      usuario: filtroUsuario,
    }));
  }, [filtroUsuario]);

  // Carregar lista de usuários e dados para os modais
  useEffect(() => {
    // Carregar usuários sempre (para filtro de admins e para menções de todos)
    carregarUsuarios();
    // Carregar dados para os modais de edição
    carregarClientes();
    carregarTiposObrigacao();
    carregarSetores();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Atualização automática a cada 10 minutos
  useEffect(() => {
    const interval = setInterval(
      () => {
        carregarDados();
      },
      10 * 60 * 1000,
    ); // 15 minutos em millisegundos

    return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const carregarUsuarios = async () => {
    try {
      const response = await usuarioService.getAll();
      setUsuarios(response.usuarios || []);
    } catch (error) {
      console.error("Erro ao carregar usuários:", error);
    }
  };

  const carregarClientes = async () => {
    try {
      const response = await clienteService.getAll({ ativo: true });
      setClientes(response.clientes || []);
    } catch (error) {
      console.error("Erro ao carregar clientes:", error);
    }
  };

  const carregarTiposObrigacao = async () => {
    try {
      const response = await tipoObrigacaoService.getAll({ limit: 200 });
      console.log(
        "Tipos de obrigação carregados:",
        response.tipos_obrigacoes?.length || 0,
      );
      setTiposObrigacao(response.tipos_obrigacoes || []);
    } catch (error) {
      console.error("Erro ao carregar tipos de obrigação:", error);
    }
  };

  const carregarSetores = async () => {
    try {
      const response = await setorService.getAll({ limit: 100 });
      setSetores(response.setores || []);
    } catch (error) {
      console.error("Erro ao carregar setores:", error);
    }
  };

  // Funções do modal de edição de tarefas
  const mostrarSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const fecharSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const abrirDialogTarefa = (tarefa) => {
    setEditingTarefa(tarefa);
    setFormDataTarefa({
      cliente_id: tarefa.cliente_id,
      descricao: tarefa.descricao,
      data_vencimento: new Date(tarefa.data_vencimento + "T00:00:00"),
      hora_vencimento: new Date(`2000-01-01T${tarefa.hora_vencimento}`),
      status: tarefa.status,
      responsavel_id: tarefa.responsavel_id || "",
      priorizar: tarefa.priorizar || false,
      comentarios: tarefa.comentarios || "",
    });
    setOpenTarefaDialog(true);
  };

  const fecharDialogTarefa = () => {
    setOpenTarefaDialog(false);
    setEditingTarefa(null);
    setFormDataTarefa({
      cliente_id: "",
      descricao: "",
      data_vencimento: new Date(),
      hora_vencimento: new Date(),
      status: "pendente",
      responsavel_id: "",
      priorizar: false,
      comentarios: "",
    });
  };

  const handleSubmitTarefa = async (e) => {
    e.preventDefault();

    if (salvandoTarefa) return; // Evita cliques duplos

    if (
      !formDataTarefa.cliente_id ||
      !formDataTarefa.descricao ||
      !formDataTarefa.data_vencimento ||
      !formDataTarefa.hora_vencimento
    ) {
      mostrarSnackbar("Todos os campos são obrigatórios", "error");
      return;
    }

    try {
      setSalvandoTarefa(true);
      // Função para formatar data sem problemas de timezone
      const formatarDataLocal = (data) => {
        const ano = data.getFullYear();
        const mes = String(data.getMonth() + 1).padStart(2, "0");
        const dia = String(data.getDate()).padStart(2, "0");
        return `${ano}-${mes}-${dia}`;
      };

      const dadosEnvio = {
        ...formDataTarefa,
        data_vencimento: formatarDataLocal(formDataTarefa.data_vencimento),
        hora_vencimento: formDataTarefa.hora_vencimento
          .toTimeString()
          .slice(0, 5),
      };

      if (editingTarefa) {
        await tarefaService.update(editingTarefa.id, dadosEnvio);
        mostrarSnackbar("Tarefa atualizada com sucesso", "success");
      }

      fecharDialogTarefa();
      // Recarregar os dados da agenda para refletir as mudanças
      carregarDados();
    } catch (error) {
      console.error("Erro ao salvar tarefa:", error);
      mostrarSnackbar("Erro ao salvar tarefa", "error");
    } finally {
      setSalvandoTarefa(false);
    }
  };

  // Funções do modal de edição de obrigações
  const abrirDialogObrigacao = (obrigacao) => {
    setEditingObrigacao(obrigacao);
    setFormDataObrigacao({
      cliente_id: obrigacao.cliente_id,
      tipo_obrigacao_id: obrigacao.tipo_obrigacao_id,
      setor_id: obrigacao.setor_id || "",
      comentarios: obrigacao.comentarios || "",
      data_vencimento: obrigacao.data_vencimento?.split("T")[0] || "",
      status: obrigacao.status,
    });
    setErrorsObrigacao({});
    setOpenObrigacaoDialog(true);
  };

  const fecharDialogObrigacao = () => {
    setOpenObrigacaoDialog(false);
    setEditingObrigacao(null);
    setFormDataObrigacao({
      cliente_id: "",
      tipo_obrigacao_id: "",
      setor_id: "",
      comentarios: "",
      data_vencimento: "",
      status: "pendente",
    });
    setErrorsObrigacao({});
  };

  // Função para calcular data de vencimento (copiada da página de Obrigações)
  const calcularDataVencimento = (tipoObrigacao) => {
    const hoje = new Date();
    const anoAtual = hoje.getFullYear();
    const mesAtual = hoje.getMonth() + 1;
    const diaAtual = hoje.getDate();

    if (tipoObrigacao.mes_recorrente) {
      // Obrigação anual
      let anoVencimento = anoAtual;
      const mesVencimento = parseInt(tipoObrigacao.mes_recorrente);
      const diaVencimento = parseInt(tipoObrigacao.dia_recorrente);

      // Se já passou da data este ano, usar o próximo ano
      if (
        mesVencimento < mesAtual ||
        (mesVencimento === mesAtual && diaVencimento < diaAtual)
      ) {
        anoVencimento = anoAtual + 1;
      }

      return `${anoVencimento}-${mesVencimento.toString().padStart(2, "0")}-${diaVencimento.toString().padStart(2, "0")}`;
    } else if (tipoObrigacao.dia_recorrente) {
      // Obrigação mensal
      let mesVencimento = mesAtual;
      let anoVencimento = anoAtual;
      const diaVencimento = parseInt(tipoObrigacao.dia_recorrente);

      // Se já passou da data este mês, usar o próximo mês
      if (diaVencimento < diaAtual) {
        mesVencimento = mesAtual + 1;
        if (mesVencimento > 12) {
          mesVencimento = 1;
          anoVencimento = anoAtual + 1;
        }
      }

      return `${anoVencimento}-${mesVencimento.toString().padStart(2, "0")}-${diaVencimento.toString().padStart(2, "0")}`;
    }

    return "";
  };

  const handleClienteChangeObrigacao = async (clienteId) => {
    setFormDataObrigacao((prev) => ({
      ...prev,
      cliente_id: clienteId,
    }));

    // Se já tem tipo de obrigação selecionado, buscar setor da recorrência
    if (clienteId && formDataObrigacao.tipo_obrigacao_id) {
      try {
        const response = await recorrenciaService.getAll({
          cliente_id: clienteId,
          tipo_obrigacao_id: formDataObrigacao.tipo_obrigacao_id,
          limit: 1,
        });

        if (response.recorrencias && response.recorrencias.length > 0) {
          const setorId = response.recorrencias[0].setor_id || "";
          setFormDataObrigacao((prev) => ({
            ...prev,
            setor_id: setorId,
          }));
        }
      } catch (error) {
        console.error("Erro ao buscar recorrência:", error);
      }
    }
  };

  const handleTipoObrigacaoChangeObrigacao = async (tipoObrigacaoId) => {
    // Encontrar o tipo de obrigação selecionado
    const tipoObrigacao = tiposObrigacao.find(
      (tipo) => tipo.id === parseInt(tipoObrigacaoId),
    );

    if (tipoObrigacao) {
      // Calcular a data de vencimento automaticamente
      const dataVencimento = calcularDataVencimento(tipoObrigacao);

      // Buscar recorrência para o cliente e tipo selecionados para puxar o setor
      let setorId = "";
      if (formDataObrigacao.cliente_id && tipoObrigacaoId) {
        try {
          const response = await recorrenciaService.getAll({
            cliente_id: formDataObrigacao.cliente_id,
            tipo_obrigacao_id: tipoObrigacaoId,
            limit: 1,
          });

          if (response.recorrencias && response.recorrencias.length > 0) {
            setorId = response.recorrencias[0].setor_id || "";
          }
        } catch (error) {
          console.error("Erro ao buscar recorrência:", error);
        }
      }

      setFormDataObrigacao((prev) => ({
        ...prev,
        tipo_obrigacao_id: tipoObrigacaoId,
        data_vencimento: dataVencimento,
        setor_id: setorId,
      }));
    } else {
      // Se nenhum tipo for selecionado, limpar a data e setor
      setFormDataObrigacao((prev) => ({
        ...prev,
        tipo_obrigacao_id: tipoObrigacaoId,
        data_vencimento: "",
        setor_id: "",
      }));
    }
  };

  const validateFormObrigacao = () => {
    const newErrors = {};

    if (!formDataObrigacao.cliente_id) {
      newErrors.cliente_id = "Cliente é obrigatório";
    }

    if (!formDataObrigacao.tipo_obrigacao_id) {
      newErrors.tipo_obrigacao_id = "Tipo de obrigação é obrigatório";
    }

    if (!formDataObrigacao.data_vencimento) {
      newErrors.data_vencimento = "Data de vencimento é obrigatória";
    }

    if (!formDataObrigacao.status) {
      newErrors.status = "Status é obrigatório";
    }

    setErrorsObrigacao(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmitObrigacao = async () => {
    if (salvandoObrigacao || !validateFormObrigacao()) return;

    try {
      setSalvandoObrigacao(true);

      if (editingObrigacao) {
        // Verificar se o status está sendo alterado para "enviado" ou "enviado_atrasado"
        const statusAlteradoParaEnviado =
          editingObrigacao.status !== "enviado" &&
          editingObrigacao.status !== "enviado_atrasado" &&
          (formDataObrigacao.status === "enviado" ||
            formDataObrigacao.status === "enviado_atrasado");

        if (statusAlteradoParaEnviado) {
          // Se o status está sendo alterado para "enviado" ou "enviado_atrasado", usar updateStatus
          const response = await obrigacaoService.updateStatus(
            editingObrigacao.id,
            formDataObrigacao.status,
          );

          // Mostrar mensagem baseada na resposta do backend
          if (response.data.proxima_obrigacao_gerada) {
            const dataVencimento = formatDate(
              response.data.proxima_obrigacao_gerada.data_vencimento,
            );
            mostrarSnackbar(
              `Obrigação atualizada com sucesso! Próxima obrigação criada automaticamente com vencimento em ${dataVencimento}`,
              "success",
            );
          } else {
            mostrarSnackbar(
              response.message || "Obrigação atualizada com sucesso",
              "success",
            );
          }
        } else {
          // Para outras alterações, usar update normal
          await obrigacaoService.update(editingObrigacao.id, formDataObrigacao);
          mostrarSnackbar("Obrigação atualizada com sucesso", "success");
        }
      }

      fecharDialogObrigacao();
      // Recarregar os dados da agenda para refletir as mudanças
      carregarDados();
    } catch (error) {
      console.error("Erro ao salvar obrigação:", error);
      mostrarSnackbar("Erro ao salvar obrigação", "error");
    } finally {
      setSalvandoObrigacao(false);
    }
  };

  const carregarDados = async () => {
    try {
      setLoading(true);
      setError("");

      // Preparar parâmetros para as obrigações
      const paramsObrigacoes = {};
      if (filtrosAplicados.datas.data_inicio) {
        paramsObrigacoes.data_inicio = filtrosAplicados.datas.data_inicio;
      }
      if (filtrosAplicados.datas.data_fim) {
        paramsObrigacoes.data_fim = filtrosAplicados.datas.data_fim;
      }
      // Adicionar filtro de usuário para as obrigações (apenas para admins)
      if (user && user.role === "admin" && filtrosAplicados.usuario) {
        paramsObrigacoes.usuario_filtro_id = filtrosAplicados.usuario;
      }

      // Carregar dados em paralelo
      const [agendaResponse, tarefasResponse] = await Promise.all([
        agendaService.getAgenda(paramsObrigacoes),
        carregarTarefasPendentes(),
      ]);

      setAgenda(agendaResponse.obrigacoes || {});
      setEstatisticas(agendaResponse.estatisticas || {});
      setTarefas(tarefasResponse.tarefas || []);
      setEstatisticasTarefas(tarefasResponse.estatisticas || {});
    } catch (err) {
      setError("Erro ao carregar agenda: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const carregarTarefasPendentes = async () => {
    try {
      // Preparar parâmetros para as tarefas (buscar pendentes e em andamento)
      const paramsTarefas = {
        status: "pendente,em_andamento",
        limit: 100,
      };

      // Se o usuário é do tipo "padrao", filtrar por responsável
      if (user && user.role === "padrao") {
        paramsTarefas.responsavel_id = user.id;
      } else if (user && user.role === "admin" && filtrosAplicados.usuario) {
        // Se é admin e tem filtro de usuário selecionado, aplicar o filtro
        paramsTarefas.responsavel_id = filtrosAplicados.usuario;
      }

      // Buscar todas as tarefas pendentes e em andamento
      const response = await tarefaService.getAll(paramsTarefas);

      // Calcular estatísticas das tarefas
      const tarefasData = response.data || [];

      const pendentes = tarefasData.filter((t) => t.status === "pendente");
      const emAndamento = tarefasData.filter(
        (t) => t.status === "em_andamento",
      );

      const urgentes = tarefasData.filter((t) => {
        const statusTarefa = getStatusTarefa(t);
        return statusTarefa === "urgente" || statusTarefa === "atraso";
      });

      const atencao = tarefasData.filter((t) => {
        return getStatusTarefa(t) === "atencao";
      });

      const estatisticas = {
        pendentes: pendentes.length,
        em_andamento: emAndamento.length,
        urgentes: urgentes.length,
        atencao: atencao.length,
      };

      return { tarefas: tarefasData, estatisticas };
    } catch (error) {
      console.error("Erro ao carregar tarefas:", error);
      return { tarefas: [], estatisticas: { pendentes: 0, urgentes: 0 } };
    }
  };

  const formatarData = (dataString) => {
    const data = new Date(dataString + "T00:00:00");
    return data.toLocaleDateString("pt-BR", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";

    // Se vier no formato yyyy-mm-dd, monta manualmente
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const [year, month, day] = dateString.split("-");
      return `${day}/${month}/${year}`;
    }

    // Se vier como string ISO (com T e Z), extrai apenas a parte da data
    if (dateString.includes("T")) {
      const datePart = dateString.split("T")[0];
      const [year, month, day] = datePart.split("-");
      return `${day}/${month}/${year}`;
    }

    // Se vier como objeto Date ou string ISO, converte para pt-BR
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return dateString; // Retorna a string original se não conseguir converter
      }
      return date.toLocaleDateString("pt-BR");
    } catch (error) {
      return dateString; // Retorna a string original em caso de erro
    }
  };

  const formatarHorario = (horarioString) => {
    return horarioString.substring(0, 5); // Remove os segundos
  };

  const ordenarTarefasPorUrgencia = (tarefasArray) => {
    const agora = new Date();

    return [...tarefasArray].sort((a, b) => {
      // Primeiro: tarefas priorizadas vêm antes
      if (a.priorizar && !b.priorizar) return -1;
      if (!a.priorizar && b.priorizar) return 1;

      // Calcular "urgência" baseada na proximidade do horário
      const calcularUrgencia = (tarefa) => {
        const dataTarefa = tarefa.data_vencimento;
        const horarioTarefa = new Date(
          dataTarefa + "T" + tarefa.hora_vencimento,
        );

        // Diferença em minutos até o horário da tarefa
        const diferencaMinutos = (horarioTarefa - agora) / (1000 * 60);

        // Se já passou, prioridade máxima (número negativo grande)
        if (diferencaMinutos < 0) return -999999 + diferencaMinutos;

        // Quanto menor a diferença, mais urgente (número menor)
        return diferencaMinutos;
      };

      // Segundo: ordenar por horário (mais próximo primeiro)
      const diff = calcularUrgencia(a) - calcularUrgencia(b);
      if (diff !== 0) return diff;

      // Terceiro: desempate estável por id para consistência
      return (a.id || 0) - (b.id || 0);
    });
  };

  const getStatusTarefa = (tarefa) => {
    const agora = new Date();
    const horarioTarefa = new Date(
      tarefa.data_vencimento + "T" + tarefa.hora_vencimento,
    );

    if (tarefa.status !== "pendente") return tarefa.status;

    // Calcular diferença em minutos até o horário da tarefa
    const diferencaMinutos = (horarioTarefa - agora) / (1000 * 60);

    // Se passou da hora do vencimento
    if (diferencaMinutos < 0) {
      return "atraso";
    }

    // Se está faltando de 0 a 15 minutos
    if (diferencaMinutos >= 0 && diferencaMinutos <= 15) {
      return "urgente";
    }

    // Se está faltando de 15 minutos até 1 hora
    if (diferencaMinutos > 15 && diferencaMinutos <= 60) {
      return "atencao";
    }

    return "pendente";
  };

  // Funções de navegação para edição
  const abrirEdicaoTarefa = (tarefaId) => {
    // Encontrar a tarefa na lista atual
    const tarefa = tarefas.find((t) => t.id === tarefaId);
    if (tarefa) {
      abrirDialogTarefa(tarefa);
    } else {
      // Se não encontrou na lista atual, buscar diretamente pela API
      const buscarTarefaEspecifica = async () => {
        try {
          const response = await tarefaService.getById(tarefaId);
          const tarefaData =
            response.data?.tarefa ||
            response.tarefa ||
            response.data ||
            response;

          if (tarefaData && tarefaData.id) {
            abrirDialogTarefa(tarefaData);
          } else {
            mostrarSnackbar("Tarefa não encontrada", "error");
          }
        } catch (error) {
          console.error("Erro ao buscar tarefa:", error);
          mostrarSnackbar("Erro ao carregar tarefa", "error");
        }
      };
      buscarTarefaEspecifica();
    }
  };

  const abrirEdicaoObrigacao = (obrigacaoId) => {
    // Buscar a obrigação diretamente pela API
    const buscarObrigacaoEspecifica = async () => {
      try {
        const response = await obrigacaoService.getById(obrigacaoId);
        const obrigacaoData =
          response.data?.obrigacao ||
          response.obrigacao ||
          response.data ||
          response;

        if (obrigacaoData && obrigacaoData.id) {
          abrirDialogObrigacao(obrigacaoData);
        } else {
          mostrarSnackbar("Obrigação não encontrada", "error");
        }
      } catch (error) {
        console.error("Erro ao buscar obrigação:", error);
        mostrarSnackbar("Erro ao carregar obrigação", "error");
      }
    };
    buscarObrigacaoEspecifica();
  };

  const limparFiltroData = () => {
    const dadosMesAtual = obterDatasMesAtual();
    setFiltroDataObrigacoes(dadosMesAtual);
    setFiltrosAplicados((prev) => ({
      ...prev,
      datas: dadosMesAtual,
    }));
  };

  // Função para aplicar o filtro de datas manualmente
  const aplicarFiltroData = () => {
    setFiltrosAplicados((prev) => ({
      ...prev,
      datas: filtroDataObrigacoes,
    }));
  };

  // Função para agrupar obrigações por tipo dentro de cada dia
  const agruparObrigacoesPorTipo = (obrigacoes) => {
    const grupos = {};

    obrigacoes.forEach((obrigacao) => {
      const tipoDescricao = obrigacao.TipoObrigacao.descricao;
      if (!grupos[tipoDescricao]) {
        grupos[tipoDescricao] = [];
      }
      grupos[tipoDescricao].push(obrigacao);
    });

    // Ordenar tipos alfabeticamente e as obrigações dentro de cada tipo por nome do cliente
    const tiposOrdenados = Object.keys(grupos).sort();
    const gruposOrdenados = {};
    tiposOrdenados.forEach((tipo) => {
      // Ordenar obrigações por nome do cliente (nome ou fantasia)
      gruposOrdenados[tipo] = grupos[tipo].sort((a, b) => {
        const nomeA = (
          a.Cliente.nome ||
          a.Cliente.fantasia ||
          ""
        ).toLowerCase();
        const nomeB = (
          b.Cliente.nome ||
          b.Cliente.fantasia ||
          ""
        ).toLowerCase();
        return nomeA.localeCompare(nomeB, "pt-BR");
      });
    });

    return gruposOrdenados;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pendente":
        return "warning";
      case "em_andamento":
        return "primary";
      case "enviado":
        return "info";
      case "enviado_atrasado":
        return "error";
      case "pago":
        return "success";
      default:
        return "default";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "pendente":
        return "Pendente";
      case "em_andamento":
        return "Em andamento";
      case "enviado":
        return "Enviado";
      case "enviado_atrasado":
        return "Enviado com atraso";
      case "pago":
        return "Pago";
      default:
        return status;
    }
  };

  // Função para determinar a cor do texto baseada na cor de fundo
  //   if (!hexColor) return "#000000";

  //   // Remove o # se existir
  //   const hex = hexColor.replace("#", "");

  //   // Converte para RGB
  //   const r = parseInt(hex.substr(0, 2), 16);
  //   const g = parseInt(hex.substr(2, 2), 16);
  //   const b = parseInt(hex.substr(4, 2), 16);

  //   // Calcula a luminância
  //   const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  //   // Retorna branco para fundos escuros, preto para fundos claros
  //   return luminance > 0.5 ? "#000000" : "#FFFFFF";
  // };

  // Componente para exibir prioridade com cor - Comentado temporariamente
  // const ExibirPrioridade = ({ prioridade }) => {
  //   // Debug temporário
  //   console.log("Prioridade na agenda:", prioridade);

  //   if (!prioridade) {
  //     return (
  //       <Typography variant="body2" color="text.secondary">
  //         Sem prioridade
  //       </Typography>
  //     );
  //   }

  //   // Se a prioridade tem cor definida, usar ela como background
  //   if (prioridade.cor) {
  //     console.log("Usando cor da prioridade na agenda:", prioridade.cor);
  //     return (
  //       <Chip
  //         label={prioridade.nome}
  //         size="small"
  //         sx={{
  //           backgroundColor: prioridade.cor,
  //           color: getContrastColor(prioridade.cor),
  //           fontWeight: 600,
  //           border: `1px solid ${prioridade.cor}`,
  //           "&:hover": {
  //           backgroundColor: prioridade.cor,
  //           opacity: 0.9,
  //           },
  //         }}
  //       />
  //     );
  //   }

  //   // Se não tem cor, usar o estilo padrão
  //   console.log("Prioridade sem cor na agenda, usando estilo padrão");
  //   return (
  //     <Chip
  //       label={prioridade.nome}
  //       size="small"
  //       color="primary"
  //       variant="outlined"
  //     />
  //   );
  // };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: "#333" }}>
          Agenda
        </Typography>
        <Alert
          severity="error"
          action={
            <Button color="inherit" onClick={carregarDados}>
              Tentar novamente
            </Button>
          }
        >
          {error}
        </Alert>
      </Box>
    );
  }

  const datasOrdenadas = Object.keys(agenda).sort();
  const tarefasOrdenadas = ordenarTarefasPorUrgencia(tarefas);

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: 600, color: "#333" }}>
          Agenda
        </Typography>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          {/* Filtros de visibilidade */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <FilterList sx={{ color: "text.secondary" }} />
            <ToggleButtonGroup
              value={filtroVisibilidade}
              exclusive
              onChange={(event, novoFiltro) => {
                if (novoFiltro !== null) {
                  setFiltroVisibilidade(novoFiltro);
                }
              }}
              size="small"
            >
              <ToggleButton value="ambos">Ambos</ToggleButton>
              <ToggleButton value="tarefas">Tarefas</ToggleButton>
              <ToggleButton value="obrigacoes">Obrigações</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Filtro por usuário (apenas para admins) */}
          {user &&
            user.role === "admin" &&
            filtroVisibilidade !== "obrigacoes" && (
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Filtrar por usuário</InputLabel>
                <Select
                  value={filtroUsuario}
                  label="Filtrar por usuário"
                  onChange={(e) => setFiltroUsuario(e.target.value)}
                >
                  <MenuItem value="">
                    <em>Todos os usuários</em>
                  </MenuItem>
                  {usuarios.map((usuario) => (
                    <MenuItem key={usuario.id} value={usuario.id}>
                      {usuario.nome}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

          {/* Estatísticas das tarefas */}
          {filtroVisibilidade !== "obrigacoes" && (
            <>
              <Card sx={{ bgcolor: "#f3e5f5", minWidth: 120 }}>
                <CardContent
                  sx={{
                    textAlign: "center",
                    py: 1,
                    pb: 0,
                    paddingBottom: "0.5rem !important",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Tarefas pendentes
                  </Typography>
                  <Typography
                    variant="h4"
                    color="purple"
                    sx={{ mb: 0, lineHeight: 1 }}
                  >
                    {(estatisticasTarefas.pendentes || 0) +
                      (estatisticasTarefas.em_andamento || 0)}
                  </Typography>
                </CardContent>
              </Card>
              <Card sx={{ bgcolor: "#ffebee", minWidth: 120 }}>
                <CardContent
                  sx={{
                    textAlign: "center",
                    py: 1,
                    pb: 0,
                    paddingBottom: "0.5rem !important",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Tarefas urgentes
                  </Typography>
                  <Typography
                    variant="h4"
                    color="error"
                    sx={{ mb: 0, lineHeight: 1 }}
                  >
                    {estatisticasTarefas.urgentes || 0}
                  </Typography>
                </CardContent>
              </Card>
            </>
          )}

          {/* Estatísticas das obrigações */}
          {filtroVisibilidade !== "tarefas" && (
            <>
              <Card sx={{ bgcolor: "#fff3e0", minWidth: 120 }}>
                <CardContent
                  sx={{
                    textAlign: "center",
                    py: 1,
                    pb: 0,
                    paddingBottom: "0.5rem !important",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Obrigações pendentes
                  </Typography>
                  <Typography
                    variant="h4"
                    color="orange"
                    sx={{ mb: 0, lineHeight: 1 }}
                  >
                    {(estatisticas.pendentes || 0) +
                      (estatisticas.em_andamento || 0)}
                  </Typography>
                </CardContent>
              </Card>
              <Card sx={{ bgcolor: "#ffebee", minWidth: 120 }}>
                <CardContent
                  sx={{
                    textAlign: "center",
                    py: 1,
                    pb: 0,
                    paddingBottom: "0.5rem !important",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Obrigações vencidas
                  </Typography>
                  <Typography
                    variant="h4"
                    color="error"
                    sx={{ mb: 0, lineHeight: 1 }}
                  >
                    {estatisticas.vencidas || 0}
                  </Typography>
                </CardContent>
              </Card>
            </>
          )}
        </Box>
      </Box>

      {/* Filtro de datas para obrigações */}
      {filtroVisibilidade !== "tarefas" && (
        <Card sx={{ mb: 3, bgcolor: "#f8f9fa" }}>
          <CardContent sx={{ py: 2 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Filtro para obrigações:
                </Typography>
                <TextField
                  label="Data início"
                  type="date"
                  size="small"
                  value={filtroDataObrigacoes.data_inicio}
                  onChange={(e) =>
                    setFiltroDataObrigacoes({
                      ...filtroDataObrigacoes,
                      data_inicio: e.target.value,
                    })
                  }
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 150 }}
                />
                <TextField
                  label="Data fim"
                  type="date"
                  size="small"
                  value={filtroDataObrigacoes.data_fim}
                  onChange={(e) =>
                    setFiltroDataObrigacoes({
                      ...filtroDataObrigacoes,
                      data_fim: e.target.value,
                    })
                  }
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 150 }}
                />
                <Button
                  variant="contained"
                  size="small"
                  onClick={aplicarFiltroData}
                  disabled={
                    filtroDataObrigacoes.data_inicio ===
                      filtrosAplicados.datas.data_inicio &&
                    filtroDataObrigacoes.data_fim ===
                      filtrosAplicados.datas.data_fim
                  }
                  sx={{ ml: 1 }}
                >
                  Filtrar
                </Button>
              </Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                {(filtroDataObrigacoes.data_inicio !==
                  obterDatasMesAtual().data_inicio ||
                  filtroDataObrigacoes.data_fim !==
                    obterDatasMesAtual().data_fim) && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={limparFiltroData}
                  >
                    Mês atual
                  </Button>
                )}
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ alignSelf: "center", ml: 1 }}
                >
                  {filtrosAplicados.datas.data_inicio !==
                    obterDatasMesAtual().data_inicio ||
                  filtrosAplicados.datas.data_fim !==
                    obterDatasMesAtual().data_fim
                    ? "Filtro personalizado ativo"
                    : "Exibindo obrigações do mês atual"}
                  {(filtroDataObrigacoes.data_inicio !==
                    filtrosAplicados.datas.data_inicio ||
                    filtroDataObrigacoes.data_fim !==
                      filtrosAplicados.datas.data_fim) && (
                    <span style={{ color: "#f57c00", marginLeft: 8 }}>
                      (clique em Filtrar para aplicar)
                    </span>
                  )}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Seção de Tarefas Urgentes */}
      {filtroVisibilidade !== "obrigacoes" && tarefasOrdenadas.length > 0 && (
        <Fade in={true} timeout={500}>
          <Card
            sx={{
              mb: 3,
              bgcolor: "#f8f4ff",
              border: "2px solid #9c27b0",
              borderRadius: 2,
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Warning sx={{ mr: 1, color: "#9c27b0", fontSize: 28 }} />
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 600, color: "#7b1fa2" }}
                >
                  Tarefas
                </Typography>
                <Chip
                  label={tarefasOrdenadas.length}
                  size="small"
                  sx={{ ml: 2, bgcolor: "#9c27b0", color: "white" }}
                />
                {filtrosAplicados.usuario && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ ml: 2 }}
                  >
                    (Filtradas por usuário)
                  </Typography>
                )}
              </Box>

              <Grid container spacing={2}>
                {tarefasOrdenadas.slice(0, 10).map((tarefa) => {
                  // Mostrar apenas as 10 mais urgentes
                  const statusTarefa = getStatusTarefa(tarefa);
                  return (
                    <Grid item xs={12} md={6} lg={4} key={tarefa.id}>
                      <Card
                        sx={{
                          height: "100%",
                          bgcolor:
                            tarefa.status === "em_andamento"
                              ? "#e3f2fd" // Azul claro para em andamento
                              : statusTarefa === "urgente"
                                ? "#fff3e0" // Laranja claro (era a cor do "atenção")
                                : statusTarefa === "atraso"
                                  ? "#ffcdd2" // Vermelho claro (mantém)
                                  : statusTarefa === "atencao"
                                    ? "#e8f5e8" // Verde claro para "atenção"
                                    : "#fff",
                          border:
                            tarefa.status === "em_andamento"
                              ? "2px solid #1976d2" // Borda azul para em andamento
                              : statusTarefa === "urgente"
                                ? "1px solid #ff9800" // Borda laranja para urgente
                                : statusTarefa === "atraso"
                                  ? "1px solid #d32f2f" // Borda vermelha escura para atraso
                                  : statusTarefa === "atencao"
                                    ? "1px solid #4caf50" // Borda verde para atenção
                                    : "1px solid #e0e0e0",
                          position: "relative",
                          boxShadow:
                            tarefa.status === "em_andamento"
                              ? "0 4px 8px rgba(25, 118, 210, 0.2)" // Sombra azul para em andamento
                              : "0 2px 4px rgba(0,0,0,0.1)",
                        }}
                      >
                        {/* Ícone de estrela para tarefas priorizadas */}
                        {tarefa.priorizar && (
                          <Box
                            sx={{
                              position: "absolute",
                              top: 8,
                              right: 8,
                              zIndex: 1,
                            }}
                          >
                            <Tooltip title="Priorizar" arrow placement="top">
                              <Star
                                sx={{
                                  color: "#ffd700",
                                  fontSize: 28,
                                  filter:
                                    "drop-shadow(0px 2px 4px rgba(0,0,0,0.4))",
                                }}
                              />
                            </Tooltip>
                          </Box>
                        )}

                        {/* Ícone para tarefas em andamento */}
                        {tarefa.status === "em_andamento" && (
                          <Box
                            sx={{
                              position: "absolute",
                              top: 8,
                              left: 8,
                              zIndex: 1,
                            }}
                          >
                            <Tooltip title="Em andamento" arrow placement="top">
                              <PlayArrow
                                sx={{
                                  color: "#1976d2",
                                  fontSize: 20,
                                  filter:
                                    "drop-shadow(0px 1px 2px rgba(0,0,0,0.3))",
                                }}
                              />
                            </Tooltip>
                          </Box>
                        )}
                        <CardContent sx={{ p: 2 }}>
                          {/* Cliente */}
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 1.5,
                            }}
                          >
                            <People
                              sx={{
                                mr: 1,
                                color: "text.secondary",
                                fontSize: 18,
                              }}
                            />
                            <Box sx={{ flexGrow: 1 }}>
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 600, fontSize: "0.875rem" }}
                              >
                                {tarefa.cliente?.nome ||
                                  tarefa.cliente?.fantasia ||
                                  "Cliente não informado"}
                              </Typography>
                            </Box>
                          </Box>

                          {/* Descrição da tarefa */}
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 1.5,
                            }}
                          >
                            <Task
                              sx={{
                                mr: 1,
                                color: "text.secondary",
                                fontSize: 18,
                              }}
                            />
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 500,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                color: "primary.main",
                                cursor: "pointer",
                                "&:hover": {
                                  textDecoration: "underline",
                                },
                              }}
                              onClick={() => abrirEdicaoTarefa(tarefa.id)}
                              title="Clique para editar a tarefa"
                            >
                              {tarefa.descricao}
                            </Typography>
                          </Box>

                          {/* Responsável */}
                          {tarefa.responsavel && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                mb: 1.5,
                              }}
                            >
                              <Person
                                sx={{
                                  mr: 1,
                                  color: "text.secondary",
                                  fontSize: 16,
                                }}
                              />
                              <Typography
                                variant="body2"
                                sx={{
                                  color: "text.secondary",
                                  fontSize: "0.75rem",
                                  fontWeight: 500,
                                }}
                              >
                                Resp.: {tarefa.responsavel.nome}
                              </Typography>
                            </Box>
                          )}

                          {/* Data e horário */}
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 1,
                            }}
                          >
                            <Schedule
                              sx={{
                                mr: 1,
                                color: "text.secondary",
                                fontSize: 16,
                              }}
                            />
                            <Typography variant="body2" color="text.secondary">
                              {new Date(
                                tarefa.data_vencimento + "T00:00:00",
                              ).toLocaleDateString("pt-BR", {
                                day: "2-digit",
                                month: "2-digit",
                              })}{" "}
                              às {formatarHorario(tarefa.hora_vencimento)}
                            </Typography>

                            {/* Mostrar status baseado na urgência */}
                            {statusTarefa === "urgente" && (
                              <Chip
                                label="Urgente!"
                                size="small"
                                sx={{
                                  ml: 1,
                                  bgcolor: "#f0453d",
                                  color: "white",
                                }}
                              />
                            )}
                            {statusTarefa === "atraso" && (
                              <Chip
                                label="Em atraso!"
                                size="small"
                                color="error"
                                icon={<Warning />}
                                sx={{ ml: 1 }}
                              />
                            )}
                            {statusTarefa === "atencao" && (
                              <Chip
                                label="Atenção"
                                size="small"
                                color="warning"
                                sx={{ ml: 1 }}
                              />
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>

              {tarefasOrdenadas.length > 10 && (
                <Box sx={{ textAlign: "center", mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    + {tarefasOrdenadas.length - 10} tarefas adicionais (acesse
                    a página de Tarefas para ver todas)
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Fade>
      )}

      {/* Obrigações por data */}
      {filtroVisibilidade !== "tarefas" && datasOrdenadas.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: "center", py: 4 }}>
            <CalendarToday
              sx={{ fontSize: 48, color: "text.secondary", mb: 2 }}
            />
            <Typography variant="h6" color="text.secondary">
              Nenhuma obrigação encontrada nos próximos dias
            </Typography>
          </CardContent>
        </Card>
      ) : (
        filtroVisibilidade !== "tarefas" && (
          <Fade in={true} timeout={300}>
            <Box>
              {datasOrdenadas.map((data) => {
                const obrigacoesPorTipo = agruparObrigacoesPorTipo(
                  agenda[data],
                );
                const tiposObrigacao = Object.keys(obrigacoesPorTipo);

                return (
                  <Accordion key={data} defaultExpanded sx={{ mb: 2 }}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 2 }}
                      >
                        <CalendarToday color="primary" />
                        <Typography variant="h6">
                          {formatarData(data)} ({agenda[data].length}{" "}
                          {agenda[data].length === 1
                            ? "obrigação"
                            : "obrigações"}
                          {tiposObrigacao.length > 1 &&
                            ` - ${tiposObrigacao.length} tipos`}
                          )
                        </Typography>
                        {filtrosAplicados.usuario && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ ml: 2 }}
                          >
                            (Filtradas por usuário)
                          </Typography>
                        )}
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      {tiposObrigacao.map((tipoDescricao) => (
                        <Accordion
                          key={`${data}-${tipoDescricao}`}
                          defaultExpanded={tiposObrigacao.length === 1}
                          sx={{
                            mb: 2,
                            bgcolor: "#f8f9fa",
                            "&:before": {
                              display: "none",
                            },
                            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                          }}
                        >
                          <AccordionSummary
                            expandIcon={<ExpandMore />}
                            sx={{
                              bgcolor: "#e9ecef",
                              "&:hover": {
                                bgcolor: "#dee2e6",
                              },
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                              }}
                            >
                              <Assignment color="action" />
                              <Typography
                                variant="subtitle1"
                                sx={{ fontWeight: 600 }}
                              >
                                {tipoDescricao}
                              </Typography>
                              <Chip
                                label={obrigacoesPorTipo[tipoDescricao].length}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails sx={{ bgcolor: "#fff" }}>
                            <Grid container spacing={2}>
                              {obrigacoesPorTipo[tipoDescricao].map(
                                (obrigacao) => (
                                  <Grid
                                    item
                                    xs={12}
                                    md={6}
                                    lg={4}
                                    key={obrigacao.id}
                                  >
                                    <Card
                                      sx={{
                                        height: "100%",
                                        bgcolor:
                                          obrigacao.status === "em_andamento"
                                            ? "#e3f2fd"
                                            : "#fff",
                                        border:
                                          obrigacao.status === "em_andamento"
                                            ? "2px solid #1976d2"
                                            : "1px solid #e0e0e0",
                                        boxShadow:
                                          obrigacao.status === "em_andamento"
                                            ? "0 4px 8px rgba(25, 118, 210, 0.2)"
                                            : "0 2px 4px rgba(0,0,0,0.1)",
                                        position: "relative",
                                      }}
                                    >
                                      <CardContent>
                                        {/* Cliente */}
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            mb: 2,
                                          }}
                                        >
                                          <People
                                            sx={{
                                              mr: 1,
                                              color: "text.secondary",
                                            }}
                                          />
                                          <Box>
                                            <Typography
                                              variant="subtitle1"
                                              sx={{ fontWeight: 600 }}
                                            >
                                              {obrigacao.Cliente.nome ||
                                                obrigacao.Cliente.fantasia}
                                            </Typography>
                                            <Typography
                                              variant="body2"
                                              color="text.secondary"
                                            >
                                              {obrigacao.Cliente.cnpj}
                                            </Typography>
                                          </Box>
                                        </Box>

                                        <Divider sx={{ my: 2 }} />

                                        {/* Tipo de obrigação */}
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            mb: 1,
                                          }}
                                        >
                                          <Assignment
                                            sx={{
                                              mr: 1,
                                              color: "text.secondary",
                                            }}
                                          />
                                          <Typography
                                            variant="body1"
                                            sx={{
                                              color: "primary.main",
                                              cursor: "pointer",
                                              "&:hover": {
                                                textDecoration: "underline",
                                              },
                                            }}
                                            onClick={() =>
                                              abrirEdicaoObrigacao(obrigacao.id)
                                            }
                                            title="Clique para editar a obrigação"
                                          >
                                            {obrigacao.TipoObrigacao.descricao}
                                          </Typography>
                                        </Box>

                                        {/* Setor */}
                                        {obrigacao.Setor && (
                                          <Box
                                            sx={{
                                              display: "flex",
                                              alignItems: "center",
                                              mb: 1,
                                            }}
                                          >
                                            <Business
                                              sx={{
                                                mr: 1,
                                                color: "text.secondary",
                                                fontSize: 16,
                                              }}
                                            />
                                            <Typography
                                              variant="body2"
                                              sx={{
                                                color: "text.secondary",
                                                fontSize: "0.75rem",
                                                fontWeight: 500,
                                              }}
                                            >
                                              Setor: {obrigacao.Setor.descricao}
                                            </Typography>
                                          </Box>
                                        )}

                                        {/* Prioridade */}
                                        {/* <Box sx={{ mb: 2 }}>
                          <ExibirPrioridade prioridade={obrigacao.Prioridade} />
                        </Box> */}

                                        {/* Ícone para obrigações em andamento */}
                                        {obrigacao.status ===
                                          "em_andamento" && (
                                          <Box
                                            sx={{
                                              position: "absolute",
                                              top: 8,
                                              right: 8,
                                              zIndex: 1,
                                            }}
                                          >
                                            <Tooltip
                                              title="Em andamento"
                                              arrow
                                              placement="top"
                                            >
                                              <PlayArrow
                                                sx={{
                                                  color: "#1976d2",
                                                  fontSize: 20,
                                                  filter:
                                                    "drop-shadow(0px 1px 2px rgba(0,0,0,0.3))",
                                                }}
                                              />
                                            </Tooltip>
                                          </Box>
                                        )}

                                        {/* Status */}
                                        <Box
                                          sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                          }}
                                        >
                                          <Chip
                                            label={getStatusLabel(
                                              obrigacao.status,
                                            )}
                                            color={getStatusColor(
                                              obrigacao.status,
                                            )}
                                            size="small"
                                          />
                                          <Typography
                                            variant="body2"
                                            color="text.secondary"
                                          >
                                            ID: {obrigacao.id}
                                          </Typography>
                                        </Box>

                                        {/* Valor se disponível */}
                                        {/* {obrigacao.valor && (
                          <Typography
                            variant="h6"
                            sx={{ mt: 2, color: "primary.main" }}
                          >
                            {new Intl.NumberFormat("pt-BR", {
                              style: "currency",
                              currency: "BRL",
                            }).format(obrigacao.valor)}
                          </Typography>
                        )} */}
                                      </CardContent>
                                    </Card>
                                  </Grid>
                                ),
                              )}
                            </Grid>
                          </AccordionDetails>
                        </Accordion>
                      ))}
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Box>
          </Fade>
        )
      )}

      {/* Mensagem quando não há dados para o filtro atual */}
      {((filtroVisibilidade === "tarefas" && tarefasOrdenadas.length === 0) ||
        (filtroVisibilidade === "obrigacoes" && datasOrdenadas.length === 0) ||
        (filtroVisibilidade === "ambos" &&
          tarefasOrdenadas.length === 0 &&
          datasOrdenadas.length === 0)) && (
        <Card>
          <CardContent sx={{ textAlign: "center", py: 4 }}>
            <CalendarToday
              sx={{ fontSize: 48, color: "text.secondary", mb: 2 }}
            />
            <Typography variant="h6" color="text.secondary">
              {filtroVisibilidade === "tarefas"
                ? "Nenhuma tarefa encontrada nos próximos dias"
                : filtroVisibilidade === "obrigacoes"
                  ? "Nenhuma obrigação encontrada nos próximos dias"
                  : "Nenhuma tarefa ou obrigação encontrada nos próximos dias"}
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Modal para editar tarefa */}
      <TarefaModal
        open={openTarefaDialog}
        onClose={fecharDialogTarefa}
        onSubmit={handleSubmitTarefa}
        formData={formDataTarefa}
        setFormData={setFormDataTarefa}
        clientes={clientes}
        usuarios={usuarios}
        usuariosEscritorio={usuarios}
        editingTarefa={editingTarefa}
        salvando={salvandoTarefa}
      />

      {/* Modal para editar obrigação */}
      <ObrigacaoModal
        open={openObrigacaoDialog}
        onClose={fecharDialogObrigacao}
        onSubmit={handleSubmitObrigacao}
        formData={formDataObrigacao}
        setFormData={setFormDataObrigacao}
        clientes={clientes}
        tiposObrigacao={tiposObrigacao}
        setores={setores}
        usuariosEscritorio={usuarios}
        editingObrigacao={editingObrigacao}
        salvando={salvandoObrigacao}
        errors={errorsObrigacao}
        onClienteChange={handleClienteChangeObrigacao}
        onTipoObrigacaoChange={handleTipoObrigacaoChangeObrigacao}
      />

      {/* Snackbar para mensagens */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={fecharSnackbar}
      >
        <Alert onClose={fecharSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Agenda;
